import React, { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import PageLayout from "@/components/Layout/PageLayout";

// Lazy-loaded page components with named chunks
const Home = lazy(() => import(/* webpackChunkName: "home" */ "@/pages/Home/Home"));
const HomeEs = lazy(() => import(/* webpackChunkName: "home-es" */ "@/pages/Home/HomeEs"));
const About = lazy(() => import(/* webpackChunkName: "about" */ "@/pages/About/About"));
const AboutEs = lazy(() => import(/* webpackChunkName: "about-es" */ "@/pages/About/AboutEs"));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ "@/pages/Contact/Contact"));
const ContactEs = lazy(() => import(/* webpackChunkName: "contact-es" */ "@/pages/Contact/ContactEs"));
const NotFound = lazy(() => import(/* webpackChunkName: "not-found" */ "@/pages/NotFound/NotFound"));

// Hire Me (for recruiters)
const HireMe = lazy(() => import(/* webpackChunkName: "hire-me" */ "@/pages/HireMe/HireMe"));
const HireMeEs = lazy(() => import(/* webpackChunkName: "hire-me-es" */ "@/pages/HireMe/HireMeEs"));

// Legal Pages
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "privacy" */ "@/pages/PrivacyPolicy/PrivacyPolicy"));
const PrivacyPolicyEs = lazy(() => import(/* webpackChunkName: "privacy-es" */ "@/pages/PrivacyPolicy/PrivacyPolicyEs"));
const TermsOfService = lazy(() => import(/* webpackChunkName: "terms" */ "@/pages/TermsOfService/TermsOfService"));
const TermsOfServiceEs = lazy(() => import(/* webpackChunkName: "terms-es" */ "@/pages/TermsOfService/TermsOfServiceEs"));

// Services Hub
const Services = lazy(() => import(/* webpackChunkName: "services" */ "@/pages/Services/Services"));
const ServicesEs = lazy(() => import(/* webpackChunkName: "services-es" */ "@/pages/Services/ServicesEs"));

// Website Development
const WebsiteDevelopment = lazy(() => import(/* webpackChunkName: "svc-webdev" */ "@/pages/Services/WebsiteDevelopment/WebsiteDevelopment"));
const WebsiteDevelopmentEs = lazy(() => import(/* webpackChunkName: "svc-webdev-es" */ "@/pages/Services/WebsiteDevelopment/WebsiteDevelopmentEs"));

// Local SEO
const LocalSEO = lazy(() => import(/* webpackChunkName: "svc-local-seo" */ "@/pages/Services/LocalSEO/LocalSEO"));
const LocalSEOEs = lazy(() => import(/* webpackChunkName: "svc-local-seo-es" */ "@/pages/Services/LocalSEO/LocalSEOEs"));

// Technical SEO (still routable but unlinked from Services index)
const TechnicalSEO = lazy(() => import(/* webpackChunkName: "svc-tech-seo" */ "@/pages/Services/TechnicalSEO/TechnicalSEO"));
const TechnicalSEOEs = lazy(() => import(/* webpackChunkName: "svc-tech-seo-es" */ "@/pages/Services/TechnicalSEO/TechnicalSEOEs"));

// Google Business Profile
const GoogleBusinessProfile = lazy(() => import(/* webpackChunkName: "svc-gbp" */ "@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfile"));
const GoogleBusinessProfileEs = lazy(() => import(/* webpackChunkName: "svc-gbp-es" */ "@/pages/Services/GoogleBusinessProfile/GoogleBusinessProfileEs"));

// Social Media Management
const SocialMediaManagement = lazy(() => import(/* webpackChunkName: "svc-social" */ "@/pages/Services/SocialMediaManagement/SocialMediaManagement"));
const SocialMediaManagementEs = lazy(() => import(/* webpackChunkName: "svc-social-es" */ "@/pages/Services/SocialMediaManagement/SocialMediaManagementEs"));

// Digital Marketing
const DigitalMarketing = lazy(() => import(/* webpackChunkName: "svc-digital" */ "@/pages/Services/DigitalMarketing/DigitalMarketing"));
const DigitalMarketingEs = lazy(() => import(/* webpackChunkName: "svc-digital-es" */ "@/pages/Services/DigitalMarketing/DigitalMarketingEs"));

// Photography
const Photography = lazy(() => import(/* webpackChunkName: "svc-photography" */ "@/pages/Services/Photography/Photography"));
const PhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-es" */ "@/pages/Services/Photography/PhotographyEs"));
const FoodPhotography = lazy(() => import(/* webpackChunkName: "svc-photography-food" */ "@/pages/Services/Photography/FoodPhotography"));
const FoodPhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-food-es" */ "@/pages/Services/Photography/FoodPhotographyEs"));
const ModelPhotography = lazy(() => import(/* webpackChunkName: "svc-photography-model" */ "@/pages/Services/Photography/ModelPhotography"));
const ModelPhotographyEs = lazy(() => import(/* webpackChunkName: "svc-photography-model-es" */ "@/pages/Services/Photography/ModelPhotographyEs"));

// Industry-specific service pillars
const LawFirmWebsites = lazy(() => import(/* webpackChunkName: "svc-law-firm-websites" */ "@/pages/Services/LawFirmWebsites/LawFirmWebsites"));
const LawFirmWebsitesEs = lazy(() => import(/* webpackChunkName: "svc-law-firm-websites-es" */ "@/pages/Services/LawFirmWebsites/LawFirmWebsitesEs"));
const EcommerceWebsites = lazy(() => import(/* webpackChunkName: "svc-ecommerce-websites" */ "@/pages/Services/EcommerceWebsites/EcommerceWebsites"));
const EcommerceWebsitesEs = lazy(() => import(/* webpackChunkName: "svc-ecommerce-websites-es" */ "@/pages/Services/EcommerceWebsites/EcommerceWebsitesEs"));

// Industry hubs (linked from the homepage IndustriesStrip; prerendered in routes-meta)
const Industries = lazy(() => import(/* webpackChunkName: "industries" */ "@/pages/Industries/Industries"));
const IndustriesEs = lazy(() => import(/* webpackChunkName: "industries-es" */ "@/pages/Industries/IndustriesEs"));
const Restaurants = lazy(() => import(/* webpackChunkName: "ind-restaurants" */ "@/pages/Industries/Restaurants"));
const RestaurantsEs = lazy(() => import(/* webpackChunkName: "ind-restaurants-es" */ "@/pages/Industries/RestaurantsEs"));
const Attorneys = lazy(() => import(/* webpackChunkName: "ind-attorneys" */ "@/pages/Industries/Attorneys"));
const AttorneysEs = lazy(() => import(/* webpackChunkName: "ind-attorneys-es" */ "@/pages/Industries/AttorneysEs"));
const IndustryEcommerce = lazy(() => import(/* webpackChunkName: "ind-ecommerce" */ "@/pages/Industries/Ecommerce"));
const IndustryEcommerceEs = lazy(() => import(/* webpackChunkName: "ind-ecommerce-es" */ "@/pages/Industries/EcommerceEs"));
const FashionBrands = lazy(() => import(/* webpackChunkName: "ind-fashion-brands" */ "@/pages/Industries/FashionBrands"));
const FashionBrandsEs = lazy(() => import(/* webpackChunkName: "ind-fashion-brands-es" */ "@/pages/Industries/FashionBrandsEs"));

// Set up a 15-min call (qualifying form before scheduling)
const Book = lazy(() => import(/* webpackChunkName: "book" */ "@/pages/Book/Book"));
const BookEs = lazy(() => import(/* webpackChunkName: "book-es" */ "@/pages/Book/BookEs"));
const BookThankYou = lazy(() => import(/* webpackChunkName: "book-thank-you" */ "@/pages/Book/BookThankYou"));
const BookThankYouEs = lazy(() => import(/* webpackChunkName: "book-thank-you-es" */ "@/pages/Book/BookThankYouEs"));

// Links hub (in-house Linktree). QR target for canvassing + IG bio.
const Links = lazy(() => import(/* webpackChunkName: "links" */ "@/pages/Links/Links"));
const LinksEs = lazy(() => import(/* webpackChunkName: "links-es" */ "@/pages/Links/LinksEs"));

// Sample monthly report (prospect-facing preview of deliverable)
const SampleReport = lazy(() => import(/* webpackChunkName: "sample-report" */ "@/pages/SampleReport/SampleReport"));
const SampleReportEs = lazy(() => import(/* webpackChunkName: "sample-report-es" */ "@/pages/SampleReport/SampleReportEs"));

// Live (Kick streaming)
const Live = lazy(() => import(/* webpackChunkName: "live" */ "@/pages/Live/Live"));
const LiveEs = lazy(() => import(/* webpackChunkName: "live-es" */ "@/pages/Live/LiveEs"));

// Loading component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-burgundy-900 border-t-transparent" />
  </div>
);

// Wrap components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// Route definitions
const routes: RouteObject[] = [
  // Chrome-free routes (no Header/Footer) — Linktree-style immersive pages.
  // Must come BEFORE the PageLayout branch so React Router matches them first.
  { path: "/links", element: withSuspense(Links) },
  { path: "/es/enlaces", element: withSuspense(LinksEs) },

  {
    element: <PageLayout />,
    children: [
      // English routes
      { path: "/", element: withSuspense(Home) },
      { path: "/about", element: withSuspense(About) },
      { path: "/contact", element: withSuspense(Contact) },
      { path: "/hire-me", element: withSuspense(HireMe) },

      // Legal Pages (English)
      { path: "/privacy-policy", element: withSuspense(PrivacyPolicy) },
      { path: "/terms-of-service", element: withSuspense(TermsOfService) },

      // Live (Kick streaming)
      { path: "/live", element: withSuspense(Live) },
      { path: "/episodes", element: <Navigate to="/live" replace /> },

      // English Service Routes
      { path: "/services", element: withSuspense(Services) },
      {
        path: "/services/website-development",
        element: withSuspense(WebsiteDevelopment),
      },
      { path: "/services/local-seo", element: withSuspense(LocalSEO) },
      { path: "/services/technical-seo", element: withSuspense(TechnicalSEO) },
      {
        path: "/services/google-business-profile",
        element: withSuspense(GoogleBusinessProfile),
      },
      {
        path: "/services/social-media-management",
        element: withSuspense(SocialMediaManagement),
      },
      {
        path: "/services/digital-marketing",
        element: withSuspense(DigitalMarketing),
      },
      {
        path: "/services/photography",
        element: withSuspense(Photography),
      },
      {
        path: "/services/photography/food",
        element: withSuspense(FoodPhotography),
      },
      {
        path: "/services/photography/model",
        element: withSuspense(ModelPhotography),
      },

      // Industry-specific service pillars (SEO-targeted)
      {
        path: "/services/law-firm-websites",
        element: withSuspense(LawFirmWebsites),
      },
      // /services/law-firm-seo merged into law-firm-websites (combined pillar)
      {
        path: "/services/law-firm-seo",
        element: <Navigate to="/services/law-firm-websites" replace />,
      },
      {
        path: "/services/ecommerce-websites",
        element: withSuspense(EcommerceWebsites),
      },

      // Industry hubs (English)
      { path: "/industries", element: withSuspense(Industries) },
      { path: "/industries/restaurants", element: withSuspense(Restaurants) },
      { path: "/industries/attorneys", element: withSuspense(Attorneys) },
      { path: "/industries/ecommerce", element: withSuspense(IndustryEcommerce) },
      { path: "/industries/fashion-brands", element: withSuspense(FashionBrands) },

      // Set up a 15-min call (canvassing-friendly short URL)
      { path: "/book", element: withSuspense(Book) },
      { path: "/book/thank-you", element: withSuspense(BookThankYou) },

      // Sample monthly report (prospect-facing)
      { path: "/sample-report", element: withSuspense(SampleReport) },

      // Spanish Live
      { path: "/es/en-vivo", element: withSuspense(LiveEs) },
      { path: "/es/episodios", element: <Navigate to="/es/en-vivo" replace /> },

      // Spanish routes (reactivated 2026-05-13 — Spanish is a strategic differentiator,
      // not a soft-archive item; restored after content audit + CTA repoint to /es/contacto form).
      { path: "/es", element: withSuspense(HomeEs) },
      { path: "/es/sobre-nosotros", element: withSuspense(AboutEs) },
      { path: "/es/contacto", element: withSuspense(ContactEs) },

      // Legal Pages (Spanish)
      {
        path: "/es/politica-de-privacidad",
        element: withSuspense(PrivacyPolicyEs),
      },
      {
        path: "/es/terminos-de-servicio",
        element: withSuspense(TermsOfServiceEs),
      },

      // Spanish Service Routes (reactivated 2026-05-13)
      { path: "/es/servicios", element: withSuspense(ServicesEs) },
      {
        path: "/es/servicios/desarrollo-web",
        element: withSuspense(WebsiteDevelopmentEs),
      },
      {
        path: "/es/servicios/seo-local",
        element: withSuspense(LocalSEOEs),
      },
      {
        path: "/es/servicios/seo-tecnico",
        element: withSuspense(TechnicalSEOEs),
      },
      {
        path: "/es/servicios/google-business-profile",
        element: withSuspense(GoogleBusinessProfileEs),
      },
      {
        path: "/es/servicios/gestion-redes-sociales",
        element: withSuspense(SocialMediaManagementEs),
      },
      {
        path: "/es/servicios/marketing-digital",
        element: withSuspense(DigitalMarketingEs),
      },
      {
        path: "/es/servicios/fotografia",
        element: withSuspense(PhotographyEs),
      },
      {
        path: "/es/servicios/fotografia/comida",
        element: withSuspense(FoodPhotographyEs),
      },
      {
        path: "/es/servicios/fotografia/modelo",
        element: withSuspense(ModelPhotographyEs),
      },
      {
        path: "/es/servicios/sitios-web-bufetes",
        element: withSuspense(LawFirmWebsitesEs),
      },
      {
        path: "/es/servicios/sitios-web-ecommerce",
        element: withSuspense(EcommerceWebsitesEs),
      },

      // Industry hubs (Spanish)
      { path: "/es/industrias", element: withSuspense(IndustriesEs) },
      { path: "/es/industrias/restaurantes", element: withSuspense(RestaurantsEs) },
      { path: "/es/industrias/abogados", element: withSuspense(AttorneysEs) },
      { path: "/es/industrias/ecommerce", element: withSuspense(IndustryEcommerceEs) },
      { path: "/es/industrias/marcas-de-moda", element: withSuspense(FashionBrandsEs) },

      // Spanish Hire Me (recruiter / freelance landing)
      { path: "/es/contratame", element: withSuspense(HireMeEs) },

      // Spanish Book funnel
      { path: "/es/agendar", element: withSuspense(BookEs) },
      { path: "/es/agendar/gracias", element: withSuspense(BookThankYouEs) },

      // Spanish Sample Report
      { path: "/es/reporte-de-muestra", element: withSuspense(SampleReportEs) },

      // 404
      { path: "*", element: withSuspense(NotFound) },
    ],
  },
];

export const router = createBrowserRouter(routes);

// Export route paths for type safety
export const routePaths = {
  // English
  home: "/",
  about: "/about",
  contact: "/contact",
  hireMe: "/hire-me",
  privacyPolicy: "/privacy-policy",
  termsOfService: "/terms-of-service",
  services: "/services",
  websiteDevelopment: "/services/website-development",
  localSeo: "/services/local-seo",
  technicalSeo: "/services/technical-seo",
  googleBusinessProfile: "/services/google-business-profile",
  socialMediaManagement: "/services/social-media-management",
  digitalMarketing: "/services/digital-marketing",
  photography: "/services/photography",
  foodPhotography: "/services/photography/food",
  modelPhotography: "/services/photography/model",
  lawFirmWebsites: "/services/law-firm-websites",
  ecommerceWebsites: "/services/ecommerce-websites",
  industries: "/industries",
  industriesRestaurants: "/industries/restaurants",
  industriesAttorneys: "/industries/attorneys",
  industriesEcommerce: "/industries/ecommerce",
  industriesFashionBrands: "/industries/fashion-brands",
  book: "/book",
  bookThankYou: "/book/thank-you",
  sampleReport: "/sample-report",
  live: "/live",
  links: "/links",

  // Spanish
  homeEs: "/es",
  aboutEs: "/es/sobre-nosotros",
  contactEs: "/es/contacto",
  privacyPolicyEs: "/es/politica-de-privacidad",
  termsOfServiceEs: "/es/terminos-de-servicio",
  serviciosEs: "/es/servicios",
  desarrolloWebEs: "/es/servicios/desarrollo-web",
  seoLocalEs: "/es/servicios/seo-local",
  seoTecnicoEs: "/es/servicios/seo-tecnico",
  googleBusinessProfileEs: "/es/servicios/google-business-profile",
  gestionRedesSocialesEs: "/es/servicios/gestion-redes-sociales",
  marketingDigitalEs: "/es/servicios/marketing-digital",
  fotografiaEs: "/es/servicios/fotografia",
  fotografiaComidaEs: "/es/servicios/fotografia/comida",
  fotografiaModeloEs: "/es/servicios/fotografia/modelo",
  sitiosWebBufetesEs: "/es/servicios/sitios-web-bufetes",
  sitiosWebEcommerceEs: "/es/servicios/sitios-web-ecommerce",
  industriasEs: "/es/industrias",
  industriasRestaurantesEs: "/es/industrias/restaurantes",
  industriasAbogadosEs: "/es/industrias/abogados",
  industriasEcommerceEs: "/es/industrias/ecommerce",
  industriasMarcasDeModaEs: "/es/industrias/marcas-de-moda",
  contratameEs: "/es/contratame",
  agendarEs: "/es/agendar",
  agendarGraciasEs: "/es/agendar/gracias",
  reporteDeMuestraEs: "/es/reporte-de-muestra",
  enVivoEs: "/es/en-vivo",
  enlacesEs: "/es/enlaces",
} as const;
